import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { isString } from '@sama/micro-dash';
import { isEmpty } from 'lodash';
import { BrowserControlService } from '../misc-services/browser-control.service';
import { MessagingService } from './messaging.service';
import { RollbarService } from './rollbar.service';

@Injectable({ providedIn: 'root' })
export class HttpErrorHandler {
  private errorHandlers = [
    this.handleUnprocessableContent.bind(this),
    this.handleConnectionTimeout.bind(this),
    this.handleConnectionError.bind(this),
    this.handleServiceUnavailable.bind(this),
    this.handleInternalServerError.bind(this),
    this.handleUnknownErrorWithMessage.bind(this),
    this.handleNotFound.bind(this),
    this.handleHttpErrorResponse.bind(this),
  ];

  constructor(
    private messagingService: MessagingService,
    private browserControlService: BrowserControlService,
    private rollbarService: RollbarService,
  ) {}

  handleError(error: unknown) {
    if (error instanceof Error) {
      // A client-side or network error occurred
      this.messagingService.showGenericError();
    } else {
      // The backend returned an unsuccessful response code
      try {
        this.errorHandlers.find((errorHandler) =>
          errorHandler(error as HttpErrorResponse),
        );
      } catch (handlerError: unknown) {
        console.error(handlerError);
      }
    }
  }

  handleUnauthorized(response: unknown) {
    if (response instanceof HttpErrorResponse && response.status === 401) {
      this.browserControlService.alert(
        response.error.message || 'Your account is not permitted to do that.',
      );
      void this.browserControlService.navigateTo(
        response.error.redirectTo || '/',
      );
    }
  }

  /*
   * Our convention is that a 422 code sent from the back-end (with an error
   * message in `error.message` and potential detailed messages in
   * `error.errors`) indicates to the front-end that the message has no internal
   * details and is safe and intended to display directly to the user, instead
   * of default error messaging.
   */
  private handleUnprocessableContent(response: HttpErrorResponse) {
    const message = response.error?.message;
    if (response.status === 422 && message) {
      if (isEmpty(response.error.errors)) {
        this.messagingService.error(message);
      } else {
        const details = response.error.errors.join(' ');
        this.messagingService.error(`${message} ${details}`);
      }

      return true;
    }
    return false;
  }

  private handleConnectionTimeout(response: HttpErrorResponse) {
    if (response.status === 408 || response.statusText === 'timeout') {
      this.messagingService.error(
        'Connection Timeout. Please wait a moment and try again.',
      );
      return true;
    }
    return false;
  }

  private handleConnectionError(response: HttpErrorResponse) {
    if (response.status <= 0) {
      this.messagingService.error(
        'Connection Error. Please check your network connection and try again.',
      );
      return true;
    }
    return false;
  }

  private handleServiceUnavailable(response: HttpErrorResponse) {
    if (
      response.status === 503 &&
      isString(response.error) &&
      response.error.includes('Maintenance')
    ) {
      this.messagingService.error(
        'Offline for maintenance. Please wait a moment and try again.',
      );
      return true;
    }
    return false;
  }

  private handleInternalServerError(response: HttpErrorResponse) {
    if (response.status === 500) {
      this.messagingService.showGenericError();
      return true;
    }
    return false;
  }

  private handleUnknownErrorWithMessage(response: HttpErrorResponse) {
    if (response.error?.message) {
      this.messagingService.error(
        response.error.message,
        response.error.errors,
      );
      return true;
    }
    return false;
  }

  private handleNotFound(response: HttpErrorResponse) {
    if (response.status === 404) {
      this.rollbarService.debug('Ajax request 404 error', {
        ajaxResponse: response,
      });
      this.messagingService.showGenericError();
      return true;
    }
    return false;
  }

  private handleHttpErrorResponse(response: HttpErrorResponse) {
    this.rollbarService.error('Ajax request failed', {
      ajaxResponse: response,
    });
    this.messagingService.showGenericError();
    return true;
  }
}
